import { useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import i18n from '@/lib/i18n';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/AuthContext';

export type AiTaskType = 'chat' | 'flashcards' | 'revision_sheet' | 'qcm' | 'clinical_case' | 'structured_consultation';
export type AiTaskStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface AiTask {
  id: string;
  user_id: string;
  task_type: AiTaskType;
  status: AiTaskStatus;
  input: Record<string, any>;
  result: Record<string, any> | null;
  error_message: string | null;
  progress: number;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

const TASK_TYPE_LABELS: Record<AiTaskType, string> = {
  chat: 'Chat IA',
  flashcards: 'Flashcards',
  revision_sheet: 'Fiche de révision',
  qcm: 'QCM',
  clinical_case: 'Cas clinique',
  structured_consultation: 'Consultation structurée',
};

const TASK_TYPE_ROUTES: Record<AiTaskType, string> = {
  chat: '/chat',
  flashcards: '/flashcards',
  revision_sheet: '/revision-sheets',
  qcm: '/study',
  clinical_case: '/study',
  structured_consultation: '/consultations',
};

const TASK_TYPE_FUNCTION: Record<string, string> = {
  flashcards: 'generate-flashcards-from-conversation',
  revision_sheet: 'generate-revision-sheet',
  qcm: 'generate-study-from-conversation',
  clinical_case: 'generate-study-from-conversation',
};

const PROCESSING_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Filters out tasks stuck in 'processing' for more than 15 minutes.
 */
function filterZombieTasks(tasks: AiTask[]): AiTask[] {
  const now = Date.now();
  return tasks.filter(t => {
    if (t.status === 'processing' && t.updated_at) {
      const updatedAt = new Date(t.updated_at).getTime();
      return (now - updatedAt) < PROCESSING_TIMEOUT_MS;
    }
    return true;
  });
}

async function fetchActiveTasks(userId: string): Promise<AiTask[]> {
  const { data } = await supabase
    .from('ai_tasks')
    .select('*')
    .eq('user_id', userId)
    .in('status', ['pending', 'processing'])
    .order('created_at', { ascending: false });

  return filterZombieTasks((data as unknown as AiTask[]) || []);
}

export function useBackgroundTasks() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const processedTaskIds = useRef<Set<string>>(new Set());

  const { data: tasks = [] } = useQuery({
    queryKey: ['ai-tasks-active', user?.id],
    queryFn: () => fetchActiveTasks(user!.id),
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const pendingCount = tasks.length;

  // Realtime subscription for live updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('ai_tasks_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ai_tasks',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const task = payload.new as unknown as AiTask;

          if (payload.eventType === 'INSERT') {
            queryClient.setQueryData<AiTask[]>(
              ['ai-tasks-active', user.id],
              (prev = []) => [task, ...prev]
            );
          }

          if (payload.eventType === 'UPDATE') {
            if (task.status === 'completed' && !processedTaskIds.current.has(task.id)) {
              processedTaskIds.current.add(task.id);
              const label = TASK_TYPE_LABELS[task.task_type] || task.task_type;
              const route = TASK_TYPE_ROUTES[task.task_type] || '/dashboard';

              toast.success(`${label} terminé !`, {
                description: 'Cliquez pour voir le résultat',
                action: {
                  label: 'Voir',
                  onClick: () => { window.location.href = route; },
                },
                duration: 8000,
              });

              // Remove from cache after short delay
              setTimeout(() => {
                queryClient.setQueryData<AiTask[]>(
                  ['ai-tasks-active', user.id],
                  (prev = []) => prev.filter(t => t.id !== task.id)
                );
              }, 2000);
            } else if (task.status === 'failed' && !processedTaskIds.current.has(task.id)) {
              processedTaskIds.current.add(task.id);
              const label = TASK_TYPE_LABELS[task.task_type] || task.task_type;
              toast.error(`${label} a échoué`, {
                description: task.error_message || 'Une erreur est survenue',
                duration: 6000,
              });

              setTimeout(() => {
                queryClient.setQueryData<AiTask[]>(
                  ['ai-tasks-active', user.id],
                  (prev = []) => prev.filter(t => t.id !== task.id)
                );
              }, 2000);
            } else {
              // Update in-place for progress changes
              queryClient.setQueryData<AiTask[]>(
                ['ai-tasks-active', user.id],
                (prev = []) => prev.map(t => t.id === task.id ? task : t)
              );
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const launchTask = useCallback(async (
    taskType: AiTaskType,
    input: Record<string, any>
  ): Promise<string | null> => {
    if (!user) return null;

    const { data: taskRow, error: insertError } = await supabase
      .from('ai_tasks')
      .insert({
        user_id: user.id,
        task_type: taskType,
        status: 'pending' as const,
        input,
      })
      .select()
      .single();

    if (insertError || !taskRow) {
      logger.error('Failed to create ai_task:', insertError);
      toast.error(i18n.t('common.backgroundTasks.launchError'));
      return null;
    }

    const taskId = (taskRow as any).id;
    const label = TASK_TYPE_LABELS[taskType] || taskType;
    toast.info(`${label} ${i18n.t('common.backgroundTasks.generating')}`, {
      description: i18n.t('common.backgroundTasks.navigateFreely'),
      duration: 4000,
    });

    const functionName = TASK_TYPE_FUNCTION[taskType];
    if (functionName) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        supabase.functions.invoke(functionName, {
          body: { ...input, taskId },
          headers: { Authorization: `Bearer ${session.access_token}` },
        }).catch(err => {
          logger.error(`Background task ${taskType} failed:`, err);
        });
      }
    }

    return taskId;
  }, [user]);

  return {
    tasks,
    pendingCount,
    launchTask,
  };
}
